import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';

/**
 * M3 (#573): cross-workspace per-member adoption analytics for one enterprise
 * partner.
 *
 * Wraps the NEW partner-scoped, SECURITY-DEFINER read RPC
 * `partner_member_analytics`, which enumerates every member across the
 * workspaces the partner provisioned (`provisioned_by_partner_id`), aggregating
 * each member's activity across ALL of the partner's workspaces. The RPC
 * re-computes authorization server-side
 * (`caller_has_partner_capability(partner_id,'analytics_read')`) and filters
 * every row to the partner's workspaces — so this hook is a thin data layer,
 * not a security boundary.
 *
 * Adoption analytics drift slowly, so this polls less aggressively than the M2
 * email-monitoring queries (no `refetchInterval`; a manual `refetch` is exposed
 * for the dashboard's Refresh button).
 */

/** One member's adoption rollup across a partner's workspaces. */
export interface PartnerMemberAnalyticsRow {
  user_id: string;
  email: string;
  /** Names of the partner's workspaces this member belongs to. */
  workspaces: string[];
  last_sign_in_at: string | null;
  never_logged_in: boolean;
  calls: number;
  /**
   * Meaningful-conversation rate (0..100), or `null` when the member made zero
   * calls — `null` is distinct from "had calls, none meaningful" (0). Mirrors
   * `get_dashboard_kpis`; render as "—".
   */
  meaningful_pct: number | null;
  contacts_created: number;
  connections_created: number;
  /** ~0 activity AND no recent login → an unused seat. */
  seat_waste: boolean;
}

/** Server default; kept here for documentation/consumers. */
const STALE_TIME_MS = 60_000;

export function useEnterpriseMemberAnalytics(partnerId: string) {
  return useQuery({
    queryKey: queryKeys.enterprisePartners.memberAnalytics(partnerId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('partner_member_analytics', {
        p_partner_id: partnerId,
      });
      if (error) throw error;
      return (data ?? []) as PartnerMemberAnalyticsRow[];
    },
    enabled: !!partnerId,
    staleTime: STALE_TIME_MS,
  });
}
